/**
 * Image URL utilities for HireHub Australia
 * 
 * Vehicle photos are stored as S3 keys (e.g., "vehicles/photo_xxx.jpg")
 * This utility converts them to proper URLs that go through the backend's
 * presigned URL redirect endpoint.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Convert an S3 key or legacy URL to a proper file access URL
 * @param {string} photoPath - S3 key like "vehicles/photo_xxx.jpg" or null/undefined
 * @param {string} folder - The storage folder (e.g., "vehicles", "verifications", "profiles")
 * @returns {string|null} - Full URL to access the file or null if invalid
 */
export const getFileUrl = (photoPath, folder = "vehicles") => {
  if (!photoPath) return null;
  
  // Already a full URL (legacy or external like OAuth profile pics)
  if (photoPath.startsWith("http://") || photoPath.startsWith("https://")) {
    return photoPath;
  }
  
  // S3 key format: "folder/filename.ext" - extract filename
  if (photoPath.includes("/")) {
    const parts = photoPath.split("/");
    const filename = parts[parts.length - 1];
    // Use the folder from the path if it matches known folders
    const pathFolder = parts[0];
    if (["vehicles", "verifications", "profiles", "checklists", "disputes"].includes(pathFolder)) {
      return `${API_URL}/api/files/${pathFolder}/${filename}`;
    }
    return `${API_URL}/api/files/${folder}/${filename}`;
  }
  
  // Just a filename
  return `${API_URL}/api/files/${folder}/${photoPath}`;
};

/**
 * Get a vehicle photo URL
 * @param {string} photoKey - S3 key like "vehicles/photo_xxx.jpg"
 * @returns {string|null} - Full URL or null
 */
export const getVehiclePhotoUrl = (photoKey) => {
  return getFileUrl(photoKey, "vehicles");
};

/**
 * Get the primary image for a vehicle, handling all legacy shapes
 * Canonical source-of-truth selector for vehicle thumbnails across the app.
 * 
 * Handles:
 * - vehicle.photos[] array (most common)
 * - vehicle.primary_photo_url (if set)
 * - vehicle.image_url (legacy)
 * - vehicle.thumbnail_url (legacy)
 * - Nested booking.vehicle shapes
 * 
 * @param {Object} vehicle - Vehicle object (or booking with embedded vehicle)
 * @returns {string|null} - Full URL to the primary image or null
 */
export const getVehiclePrimaryImage = (vehicle) => {
  if (!vehicle) return null;
  
  // Priority 1: primary_photo_url if explicitly set (full URL or S3 key)
  if (vehicle.primary_photo_url) {
    return getVehiclePhotoUrl(vehicle.primary_photo_url);
  }
  
  // Priority 2: First valid photo from photos array
  if (Array.isArray(vehicle.photos) && vehicle.photos.length > 0) {
    const validPhotos = filterValidPhotos(vehicle.photos);
    if (validPhotos.length > 0) {
      return getVehiclePhotoUrl(validPhotos[0]);
    }
  }
  
  // Priority 3: Legacy field names
  if (vehicle.image_url) {
    return getVehiclePhotoUrl(vehicle.image_url);
  }
  
  if (vehicle.thumbnail_url) {
    return getVehiclePhotoUrl(vehicle.thumbnail_url);
  }
  
  // Priority 4: Check nested vehicle object (for booking.vehicle shapes)
  if (vehicle.vehicle && typeof vehicle.vehicle === 'object') {
    return getVehiclePrimaryImage(vehicle.vehicle);
  }
  
  return null;
};

/**
 * Handle image load error in dev mode - logs the failed URL for debugging
 * @param {Event} event - The error event
 * @param {string} vehicleId - The vehicle ID (optional)
 */
export const handleVehicleImageError = (event, vehicleId = null) => {
  if (process.env.NODE_ENV === 'development') {
    const attemptedUrl = event?.target?.src || 'unknown';
  }
  // Hide the broken image
  if (event?.target) {
    event.target.style.display = 'none';
  }
};

/**
 * Get a verification document URL
 * @param {string} docKey - S3 key like "verifications/doc_xxx.jpg"
 * @returns {string|null} - Full URL or null
 */
export const getVerificationDocUrl = (docKey) => {
  return getFileUrl(docKey, "verifications");
};

/**
 * Get a profile photo URL
 * @param {string} photoKey - S3 key like "profiles/profile_xxx.jpg" or OAuth URL
 * @returns {string|null} - Full URL or null
 */
export const getProfilePhotoUrl = (photoKey) => {
  return getFileUrl(photoKey, "profiles");
};

/**
 * Filter out null/undefined photos from an array
 * @param {Array} photos - Array of photo paths that may contain nulls
 * @returns {Array} - Filtered array with only valid photo paths
 */
export const filterValidPhotos = (photos) => {
  if (!Array.isArray(photos)) return [];
  return photos.filter(p => p && typeof p === "string" && p.trim().length > 0);
};

export default {
  getFileUrl,
  getVehiclePhotoUrl,
  getVehiclePrimaryImage,
  handleVehicleImageError,
  getVerificationDocUrl,
  getProfilePhotoUrl,
  filterValidPhotos
};
